<script lang="ts">
  import { autofocus } from '#app/lib/components/actions/autofocus'
  import { autosize } from '#app/lib/components/actions/autosize'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { getActionKey } from '#app/lib/key_events'
  import { userContent } from '#app/lib/user_content'
  import { isSelectionClick } from '#app/lib/utils'
  import type { ClickEvent } from '#app/types/declarations/svelte'
  import { updateItems } from '#inventory/lib/item_actions'
  import type { SerializedItemWithPrivateData } from '#inventory/lib/items'
  import { i18n, I18n } from '#user/lib/i18n'

  let {
    item = $bindable(),
    flash = $bindable(),
  }: {
    item: SerializedItemWithPrivateData
    flash?: FlashState
  } = $props()

  let { notes = '' } = $state(item)

  let editMode = $state(false)

  async function save () {
    editMode = false
    try {
      await updateItems({
        items: [ item ],
        attribute: 'notes',
        value: notes,
      })
      item.notes = notes
    } catch (err) {
      flash = err
    }
  }

  function onKeyDown (e) {
    const key = getActionKey(e)
    if (key === 'esc') editMode = false
    else if (e.ctrlKey && key === 'enter') save()
    e.stopPropagation()
  }

  function onEditButtonClick (e: ClickEvent) {
    if (isSelectionClick(e)) return
    editMode = true
  }
</script>

{#snippet header()}
  <div class="header">
    <span class="section-label">{I18n('private notes')}</span>
    <span class="indicator" title={I18n('this is visible by anyone who can see this item')}>
      {@html icon('lock')}
    </span>
    <span>{@html icon('pencil')}</span>
  </div>
{/snippet}

{#if editMode}
  <div class="item-notes">
    {@render header()}
    <textarea
      placeholder={`(${i18n('notes_placeholder')})`}
      bind:value={notes}
      use:autofocus
      use:autosize
      onkeydown={onKeyDown}
    ></textarea>
    <div class="button-group-right">
      <button
        class="tiny-button cancel"
        onclick={() => editMode = false}
      >
        {i18n('cancel')}
      </button>
      <button
        class="tiny-button success"
        onclick={save}
      >
        {i18n('save')}
      </button>
    </div>
  </div>
{:else}
  <button
    class="item-notes"
    title={I18n('edit')}
    onclick={onEditButtonClick}
  >
    {@render header()}
    <div class="text">
      <p>{@html userContent(notes)}</p>
    </div>
  </button>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .item-notes{
    display: block;
    width: 100%;
    background-color: $dark-grey;
    padding: 0.5em 1em;
    margin: 0.4em 0 0.6em;
    line-height: 1.6rem;
    @include radius;
    &, .text, :global(.fa){
      color: white;
    }
  }
  .section-label{
    color: $soft-grey;
  }
  .text{
    display: block;
    padding: 0;
    user-select: text;
    p{
      text-align: start;
    }
  }
  .tiny-button{
    line-height: inherit;
  }
  .header{
    @include display-flex(row, center, center);
    height: 2rem;
  }
  .indicator{
    margin-inline-start: auto;
  }
</style>
